import styled from "styled-components";
import { spacing } from "../../../design-system/tokens/spacing";
import { typographyMixin } from "../../../design-system/tokens/typography";
import { rawColors } from "../../../design-system/tokens/colors";
import { borderRadius } from "../../../design-system/tokens/borders";
import { Button } from "../../atoms";

/**
 * Body wrapper — Figma uses 12px horizontal padding, 16px top, 16px bottom.
 *
 * `overflow-x: hidden` is load-bearing: the design-system ModalBody applies
 * `overflow-y: auto`, which (per CSS spec) implicitly turns `overflow-x` from
 * `visible` to `auto`. Any 1px subpixel rounding in the step indicator or
 * card grid then triggers a horizontal scrollbar. Clipping it here at the
 * stack level prevents that scrollbar from ever surfacing while still
 * allowing vertical scroll on the parent ModalBody.
 *
 * Bottom padding is bumped to 16px (vs Figma's 12px) so the form card's
 * bottom border has breathing room from the modal-footer separator —
 * otherwise the divider visually merges with the card border.
 */
export const BodyStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
  width: 100%;
  min-width: 0;
  max-width: 100%;
  flex: 1 1 auto;
  min-height: 0;
  padding: ${spacing[4]} ${spacing[3]};
  box-sizing: border-box;
  overflow-x: hidden;
`;

/** Locks the step-content region to the tallest measured step so far.
 * This removes step-to-step height jumps without forcing scrollbars. */
export const StepBodyViewport = styled.div<{ $minHeightPx?: number }>`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  min-height: ${({ $minHeightPx }) =>
    typeof $minHeightPx === "number" ? `${$minHeightPx}px` : "0"};
`;

/** Flex wrapper around the active step so scroll regions can fill the modal
 * body and only overflow-scroll once content exceeds that space. */
export const StepBodyMeasure = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
`;

/** Footer row — overrides the design-system ModalFooter alignment so Back
 *  sits left and the primary action sits right (footer's default is
 *  flex-end). When there's no Back button, the primary action sits right. */
export const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

export const BiSelectFooterRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  column-gap: ${spacing[3]};
  width: 100%;
`;

export const BiSelectLeftSlot = styled.div`
  justify-self: start;
`;

export const BiSelectCenterSlot = styled.div`
  justify-self: end;
`;

export const BiSelectRightSlot = styled.div`
  justify-self: end;
`;

export const FooterRightOnly = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
`;

/** Indicator wrap — the Figma indicator sits in a 12px horizontal inset on
 *  top of the body. Width-constrained so it never forces horizontal scroll. */
export const IndicatorWrap = styled.div`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
`;

/** Empty / coming-soon panel for connectors without a config yet. */
export const ComingSoonCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spacing[2]};
  width: 100%;
  min-height: 240px;
  padding: ${spacing[6]};
  background-color: ${rawColors.structural.white};
  border: 1px dashed ${rawColors.stroke[300]};
  border-radius: ${borderRadius.xl};
`;

export const ComingSoonTitle = styled.p`
  margin: 0;
  ${typographyMixin("body", 2)}
  color: ${rawColors.text.highEmphasis};
`;

export const ComingSoonSubtitle = styled.p`
  margin: 0;
  ${typographyMixin("body", 4)}
  color: ${rawColors.text.mediumEmphasis};
  text-align: center;
`;

/**
 * Outline-primary button — Figma's "Test connection" / "Back" / "Import"
 * style: white bg, 1px primary[500] border, primary[500] text, no shadow,
 * 36px height, 8px radius. The atom-level Button doesn't currently expose
 * this exact variant (the closest, `link-pill`, uses a stroke-200 border),
 * so we extend it here with `&&&` to win specificity over variant CSS.
 *
 * Kept inside the wizard organism (not promoted to the design-system Button)
 * to keep this fix scope-local — easy to lift into the atom later if other
 * surfaces want the same treatment.
 */
export const OutlinePrimaryButton = styled(Button).attrs({
  variant: "link-pill" as const,
  size: "md" as const,
})`
  &&& {
    height: 36px;
    padding: ${spacing[2]} ${spacing[3]};
    background-color: ${rawColors.structural.white};
    border: 1px solid ${rawColors.primary[500]};
    color: ${rawColors.primary[500]};
    box-shadow: none;
    border-radius: ${borderRadius.lg};

    &:hover:not(:disabled) {
      background-color: ${rawColors.primary[100]};
      border-color: ${rawColors.primary[500]};
      color: ${rawColors.primary[500]};
    }

    &:active:not(:disabled) {
      background-color: ${rawColors.primary[100]};
    }

    &:disabled {
      color: ${rawColors.text.lowEmphasis};
      border-color: ${rawColors.stroke[300]};
      cursor: not-allowed;
    }

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

/** Layout-only placeholder used to reserve footer height on Finish step
 * without showing extra actions. */
export const HiddenFooterAction = styled(OutlinePrimaryButton)`
  &&& {
    visibility: hidden;
    pointer-events: none;
  }
`;
